// =============================================================================
// MeuDriver — Student Profile Edit
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Camera, Save, KeyRound, Trash2, AlertTriangle, Loader2, User, Star, ThumbsUp, Bell, Target, Medal } from 'lucide-react'

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { useStudentProfileDetails, useUpdateStudentProfile } from '@/hooks/useStudentProfile'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { getInitials } from '@/lib/utils'
import { BRAZILIAN_STATES } from '@/lib/constants'
import { StudentAddressesCard } from '@/components/student/StudentAddressesCard'
import { PushNotificationToggle } from '@/components/shared/PushNotificationToggle'
import { TwoFactorSetupCard } from '@/components/shared/TwoFactorSetupCard'
import { GoogleCalendarConnect } from '@/components/student/GoogleCalendarConnect'
import { NotificationPreferencesForm } from '@/components/shared/NotificationPreferencesForm'
import { LgpdRequestForm } from '@/components/shared/LgpdRequestForm'
import { useMyStudentReviews } from '@/hooks/useStudentReviews'
import { BadgeShowcase } from '@/components/student/BadgeShowcase'
import { DataPortabilityCard } from '@/components/shared/DataPortabilityCard'

// ─── Validation schemas ───────────────────────────────────────────────────────

const profileSchema = z.object({
  full_name: z
    .string({ required_error: 'Nome completo é obrigatório' })
    .min(3, 'O nome deve ter pelo menos 3 caracteres')
    .max(100, 'O nome deve ter no máximo 100 caracteres'),
  phone: z
    .string()
    .regex(/^\+?[\d\s\-().]{10,20}$/, 'Número de telefone inválido')
    .optional()
    .or(z.literal('')),
  city: z
    .string()
    .max(80, 'Cidade deve ter no máximo 80 caracteres')
    .optional()
    .or(z.literal('')),
  state: z
    .string()
    .max(2, 'Selecione um estado válido')
    .optional()
    .or(z.literal('')),
})

const passwordSchema = z
  .object({
    newPassword: z
      .string({ required_error: 'Nova senha é obrigatória' })
      .min(6, 'A senha deve ter pelo menos 6 caracteres'),
    confirmPassword: z
      .string({ required_error: 'Confirmação de senha é obrigatória' }),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'As senhas não conferem',
    path: ['confirmPassword'],
  })

type ProfileFormData = z.infer<typeof profileSchema>
type PasswordFormData = z.infer<typeof passwordSchema>

// ─── Avatar Upload ────────────────────────────────────────────────────────────

interface AvatarUploadProps {
  currentUrl: string | null
  userId: string
  name: string
  onUploaded: (url: string) => void
}

function AvatarUpload({ currentUrl, userId, name, onUploaded }: AvatarUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentUrl)

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    // Client-side validation
    if (file.size > 2 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 2 MB.')
      return
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato inválido. Use JPG, PNG ou WEBP.')
      return
    }

    // Optimistic preview
    const objectUrl = URL.createObjectURL(file)
    setPreviewUrl(objectUrl)
    setUploading(true)

    try {
      const ext = file.name.split('.').pop() ?? 'jpg'
      const filePath = `${userId}/avatar.${ext}`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true, contentType: file.type })

      if (uploadError) throw new Error(uploadError.message)

      const { data } = supabase.storage.from('avatars').getPublicUrl(filePath)
      const publicUrl = `${data.publicUrl}?t=${Date.now()}`

      // Persist avatar_url to profile row
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', userId)

      if (updateError) throw new Error(updateError.message)

      setPreviewUrl(publicUrl)
      onUploaded(publicUrl)
      toast.success('Foto de perfil atualizada!')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao enviar imagem.'
      toast.error(msg)
      setPreviewUrl(currentUrl)
    } finally {
      setUploading(false)
      URL.revokeObjectURL(objectUrl)
    }
  }

  return (
    <div className="flex items-center gap-6">
      <div className="relative">
        <Avatar className="h-20 w-20">
          <AvatarImage src={previewUrl ?? undefined} />
          <AvatarFallback className="bg-primary/10 text-primary text-2xl font-semibold">
            {getInitials(name || 'Aluno')}
          </AvatarFallback>
        </Avatar>
        {uploading && (
          <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          </div>
        )}
      </div>
      <div className="space-y-1">
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Camera className="h-4 w-4" />
          {uploading ? 'Enviando...' : 'Alterar foto'}
        </button>
        <p className="text-xs text-muted-foreground">JPG, PNG ou WEBP — máx. 2 MB</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </div>
  )
}

// ─── Delete Account Dialog ────────────────────────────────────────────────────

interface DeleteAccountDialogProps {
  open: boolean
  onClose: () => void
}

function DeleteAccountDialog({ open, onClose }: DeleteAccountDialogProps) {
  const { signOut } = useAuth()
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const CONFIRMATION_WORD = 'EXCLUIR'

  async function handleDelete() {
    setDeleting(true)
    try {
      // In a production app this would call a Supabase Edge Function that
      // deletes the user via the Admin API. Here we sign out as a safe fallback.
      toast.info('Solicitação de exclusão enviada. Nossa equipe processará em até 48h.')
      await signOut()
    } catch {
      toast.error('Erro ao processar exclusão.')
    } finally {
      setDeleting(false)
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Excluir conta
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">
              <strong>Atenção:</strong> Esta ação é <strong>irreversível</strong>. Todos os seus dados,
              histórico de aulas e avaliações serão permanentemente excluídos.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-delete" className="text-sm">
              Digite <strong>{CONFIRMATION_WORD}</strong> para confirmar:
            </Label>
            <Input
              id="confirm-delete"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder={CONFIRMATION_WORD}
              className="font-mono"
            />
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} disabled={deleting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={confirmText !== CONFIRMATION_WORD || deleting}
              onClick={handleDelete}
            >
              {deleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Excluindo...</>
              ) : (
                'Excluir minha conta'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── Form field helper ────────────────────────────────────────────────────────

interface FieldProps {
  id: string
  label: string
  error?: string
  children: React.ReactNode
}

function Field({ id, label, error, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

// ─── Student Ratings Received ────────────────────────────────────────────────

interface RatingEntry {
  id: string
  punctuality: number
  behavior: number
  attention: number
  comment: string | null
  created_at: string
  instructor: { full_name: string | null; avatar_url: string | null } | null
}

function StarRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`h-3.5 w-3.5 ${i < value ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
          />
        ))}
        <span className="ml-1 text-xs font-medium text-gray-600">{value}/5</span>
      </div>
    </div>
  )
}

function StudentRatingsCard({ userId }: { userId: string }) {
  const { data: ratings, isLoading } = useQuery<RatingEntry[]>({
    queryKey: ['student-ratings-received', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_ratings')
        .select('id, punctuality, behavior, attention, comment, created_at, instructor:profiles!student_ratings_instructor_id_fkey(full_name, avatar_url)')
        .eq('student_id', userId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as RatingEntry[]
    },
  })

  const avg = ratings && ratings.length > 0
    ? ratings.reduce((acc, r) => acc + (r.punctuality + r.behavior + r.attention) / 3, 0) / ratings.length
    : null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ThumbsUp className="h-4 w-4 text-primary" />
          Avaliações recebidas de instrutores
          {avg !== null && (
            <span className="ml-auto flex items-center gap-1 text-sm font-normal text-amber-500">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              {avg.toFixed(1)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !ratings || ratings.length === 0 ? (
          <div className="flex flex-col items-center py-8 gap-2 text-center text-muted-foreground">
            <Star className="h-8 w-8 opacity-20" />
            <p className="text-sm">Nenhuma avaliação de instrutor ainda.</p>
            <p className="text-xs opacity-70">Avaliações aparecem aqui após concluir aulas.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {ratings.map((r) => (
              <div key={r.id} className="rounded-xl border border-gray-100 p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={r.instructor?.avatar_url ?? undefined} />
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                      {getInitials(r.instructor?.full_name ?? 'I')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{r.instructor?.full_name ?? 'Instrutor'}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <StarRow label="Pontualidade" value={r.punctuality} />
                  <StarRow label="Comportamento" value={r.behavior} />
                  <StarRow label="Atenção" value={r.attention} />
                </div>
                {r.comment && (
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 italic">
                    "{r.comment}"
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Q4: StudentSimpleReviewsCard ────────────────────────────────────────────
// Exibe avaliações (rating 1-5 + comentário) recebidas via instructor_student_reviews

function StudentSimpleReviewsCard() {
  const { data: reviews = [], isLoading } = useMyStudentReviews()

  const avg = reviews.length > 0
    ? reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length
    : null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Star className="h-4 w-4 text-primary" />
          Avaliações de desempenho
          {avg !== null && (
            <span className="ml-auto flex items-center gap-1 text-sm font-normal text-amber-500">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              {avg.toFixed(1)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="flex flex-col items-center py-8 gap-2 text-center text-muted-foreground">
            <Star className="h-8 w-8 opacity-20" />
            <p className="text-sm">Nenhuma avaliação de desempenho ainda.</p>
            <p className="text-xs opacity-70">Os instrutores avaliam você após cada aula concluída.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((r) => (
              <div key={r.id} className="rounded-xl border border-gray-100 p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={r.instructor_avatar ?? undefined} />
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                      {getInitials(r.instructor_name ?? 'I')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {r.instructor_name?.split(' ')[0] ?? 'Instrutor'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString('pt-BR', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${i < r.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`}
                      />
                    ))}
                    <span className="ml-1 text-xs font-semibold text-gray-600">{r.rating}/5</span>
                  </div>
                </div>
                {r.comment && (
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 italic leading-relaxed">
                    "{r.comment}"
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── R3: StudentLearningGoalsCard ─────────────────────────────────────────────

const LICENSE_GOAL_OPTIONS = [
  { value: 'A',  label: 'A — Motocicleta' },
  { value: 'B',  label: 'B — Carro' },
  { value: 'AB', label: 'AB — Carro e Moto' },
  { value: 'C',  label: 'C — Caminhão' },
  { value: 'D',  label: 'D — Ônibus' },
  { value: 'E',  label: 'E — Carreta' },
] as const

const LEVEL_OPTIONS = [
  { value: 'beginner',     label: 'Iniciante — nunca dirigi' },
  { value: 'intermediate', label: 'Intermediário — alguma prática' },
  { value: 'advanced',     label: 'Avançado — já dirijo há anos' },
] as const

function StudentLearningGoalsCard() {
  const { data, isLoading } = useStudentProfileDetails()
  const update = useUpdateStudentProfile()

  const [licenseGoal, setLicenseGoal] = useState<string>(data?.license_goal ?? '')
  const [currentLevel, setCurrentLevel] = useState<string>(data?.current_level ?? '')
  const [learningNotes, setLearningNotes] = useState<string>(data?.learning_notes ?? '')
  const [preferredSchedule, setPreferredSchedule] = useState<string>(data?.preferred_schedule ?? '')

  // Sync quando os dados chegam
  useEffect(() => {
    if (data) {
      setLicenseGoal(data.license_goal ?? '')
      setCurrentLevel(data.current_level ?? '')
      setLearningNotes(data.learning_notes ?? '')
      setPreferredSchedule(data.preferred_schedule ?? '')
    }
  }, [data])

  async function handleSave() {
    try {
      await update.mutateAsync({
        license_goal:       licenseGoal       || null,
        current_level:      currentLevel      || null,
        learning_notes:     learningNotes     || null,
        preferred_schedule: preferredSchedule || null,
      })
      toast.success('Objetivos salvos com sucesso!')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar objetivos.'
      toast.error(msg)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          Meus objetivos de aprendizado
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="license_goal">Tipo de habilitação desejada</Label>
                <Select
                  value={licenseGoal}
                  onValueChange={setLicenseGoal}
                >
                  <SelectTrigger id="license_goal">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {LICENSE_GOAL_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="current_level">Nível atual</Label>
                <Select
                  value={currentLevel}
                  onValueChange={setCurrentLevel}
                >
                  <SelectTrigger id="current_level">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVEL_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="preferred_schedule">Horário preferido</Label>
              <Input
                id="preferred_schedule"
                placeholder="Ex: manhãs e fins de semana"
                value={preferredSchedule}
                onChange={(e) => setPreferredSchedule(e.target.value)}
                maxLength={50}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="learning_notes">
                Notas de preferências
                <span className="ml-1 text-xs text-muted-foreground font-normal">
                  ({learningNotes.length}/1000)
                </span>
              </Label>
              <Textarea
                id="learning_notes"
                placeholder="Ex: Tenho medo de estrada, prefiro começar em ruas tranquilas..."
                rows={3}
                value={learningNotes}
                onChange={(e) => setLearningNotes(e.target.value)}
                maxLength={1000}
              />
            </div>

            <div className="pt-1">
              <Button
                onClick={handleSave}
                disabled={update.isPending}
                className="gap-2"
                data-testid="save-learning-goals-btn"
              >
                {update.isPending
                  ? <><Loader2 className="h-4 w-4 animate-spin" />Salvando...</>
                  : <><Save className="h-4 w-4" />Salvar objetivos</>
                }
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function StudentProfile() {
  const { user, profile, refreshProfile } = useAuth()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile?.avatar_url ?? null)

  // ── Profile form ─────────────────────────────────────────────────────────────
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    setValue: setProfileValue,
    watch: watchProfile,
    formState: { errors: profileErrors, isSubmitting: isSavingProfile },
    reset: resetProfile,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: profile?.full_name ?? '',
      phone: profile?.phone ?? '',
      city: profile?.city ?? '',
      state: profile?.state ?? '',
    },
  })

  // ── Password form ────────────────────────────────────────────────────────────
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors, isSubmitting: isSavingPassword },
    reset: resetPassword,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  // Sync form defaults when profile loads
  useEffect(() => {
    if (profile) {
      resetProfile({
        full_name: profile.full_name ?? '',
        phone: profile.phone ?? '',
        city: profile.city ?? '',
        state: profile.state ?? '',
      })
      setAvatarUrl(profile.avatar_url)
    }
  }, [profile, resetProfile])

  const selectedState = watchProfile('state')

  // ── Save profile ─────────────────────────────────────────────────────────────
  async function onSaveProfile(data: ProfileFormData) {
    if (!user) return
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: data.full_name,
          phone: data.phone || null,
          city: data.city || null,
          state: data.state || null,
        })
        .eq('id', user.id)

      if (error) throw new Error(error.message)

      await refreshProfile()
      toast.success('Perfil atualizado com sucesso!')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar perfil.'
      toast.error(msg)
    }
  }

  // ── Save password ────────────────────────────────────────────────────────────
  async function onSavePassword(data: PasswordFormData) {
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword,
      })
      if (error) throw new Error(error.message)

      toast.success('Senha alterada com sucesso!')
      resetPassword()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao alterar senha.'
      toast.error(msg)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto space-y-8">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <User className="h-6 w-6 text-primary" />
          Meu perfil
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Gerencie suas informações pessoais e preferências de conta.
        </p>
      </div>

      {/* ── Avatar + Profile form ───────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Informações pessoais</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar upload */}
          {user && (
            <AvatarUpload
              currentUrl={avatarUrl}
              userId={user.id}
              name={profile?.full_name ?? ''}
              onUploaded={(url) => setAvatarUrl(url)}
            />
          )}

          <Separator />

          {/* Profile fields */}
          <form onSubmit={handleProfileSubmit(onSaveProfile)} className="space-y-5">
            <Field id="full_name" label="Nome completo" error={profileErrors.full_name?.message}>
              <Input
                id="full_name"
                placeholder="Seu nome completo"
                {...registerProfile('full_name')}
                className={profileErrors.full_name ? 'border-red-400' : ''}
              />
            </Field>

            <Field id="phone" label="Telefone" error={profileErrors.phone?.message}>
              <Input
                id="phone"
                type="tel"
                placeholder="(11) 91234-5678"
                {...registerProfile('phone')}
                className={profileErrors.phone ? 'border-red-400' : ''}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="city" label="Cidade" error={profileErrors.city?.message}>
                <Input
                  id="city"
                  placeholder="Sua cidade"
                  {...registerProfile('city')}
                  className={profileErrors.city ? 'border-red-400' : ''}
                />
              </Field>

              <Field id="state" label="Estado" error={profileErrors.state?.message}>
                <Select
                  value={selectedState ?? ''}
                  onValueChange={(val) => setProfileValue('state', val)}
                >
                  <SelectTrigger id="state" className={profileErrors.state ? 'border-red-400' : ''}>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {BRAZILIAN_STATES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {/* Read-only email */}
            <Field id="email" label="E-mail">
              <Input
                id="email"
                type="email"
                value={user?.email ?? ''}
                disabled
                className="bg-gray-50 text-muted-foreground cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground">
                O e-mail não pode ser alterado diretamente. Contate o suporte se necessário.
              </p>
            </Field>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={isSavingProfile}
                className="gap-2"
              >
                {isSavingProfile ? (
                  <><Loader2 className="h-4 w-4 animate-spin" />Salvando...</>
                ) : (
                  <><Save className="h-4 w-4" />Salvar alterações</>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ── Change password ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            Alterar senha
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit(onSavePassword)} className="space-y-5">
            <Field
              id="newPassword"
              label="Nova senha"
              error={passwordErrors.newPassword?.message}
            >
              <Input
                id="newPassword"
                type="password"
                placeholder="Mínimo 6 caracteres"
                autoComplete="new-password"
                {...registerPassword('newPassword')}
                className={passwordErrors.newPassword ? 'border-red-400' : ''}
              />
            </Field>

            <Field
              id="confirmPassword"
              label="Confirmar nova senha"
              error={passwordErrors.confirmPassword?.message}
            >
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Repita a nova senha"
                autoComplete="new-password"
                {...registerPassword('confirmPassword')}
                className={passwordErrors.confirmPassword ? 'border-red-400' : ''}
              />
            </Field>

            <div className="pt-2">
              <Button
                type="submit"
                variant="outline"
                disabled={isSavingPassword}
                className="gap-2"
              >
                {isSavingPassword ? (
                  <><Loader2 className="h-4 w-4 animate-spin" />Alterando...</>
                ) : (
                  <><KeyRound className="h-4 w-4" />Alterar senha</>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ── 2FA / Autenticação em dois fatores ──────────────────────────────── */}
      <TwoFactorSetupCard />

      {/* ── Saved addresses ─────────────────────────────────────────────────── */}
      <StudentAddressesCard />

      {/* ── Notificações ─────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            Notificações
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Push device toggle (habilita/desabilita o dispositivo) */}
          <PushNotificationToggle label="Ativar notificações push neste dispositivo" />
          {/* Preferências por categoria (e-mail + push) */}
          <NotificationPreferencesForm />
        </CardContent>
      </Card>

      {/* ── Google Calendar ─────────────────────────────────────────────────── */}
      <GoogleCalendarConnect />

      {/* ── Ratings received from instructors (student_ratings) ─────────────── */}
      <StudentRatingsCard userId={user?.id ?? ''} />

      {/* ── Q4: Avaliações recebidas (instructor_student_reviews) ────────────── */}
      <StudentSimpleReviewsCard />

      {/* ── R3: Objetivos de aprendizado ─────────────────────────────────────── */}
      <StudentLearningGoalsCard />

      {/* ── S3: Emblemas ─────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Medal className="h-4 w-4 text-amber-500" />
            Meus emblemas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BadgeShowcase studentId={user?.id} />
        </CardContent>
      </Card>

      {/* ── S4: Portabilidade de dados ────────────────────────────────────────── */}
      <DataPortabilityCard />

      {/* ── LGPD ────────────────────────────────────────────────────────────── */}
      <LgpdRequestForm />

      {/* ── Danger zone ─────────────────────────────────────────────────────── */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-base text-red-700 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Zona de perigo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div>
              <p className="font-medium text-sm text-gray-800">Excluir minha conta</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Todos os seus dados serão permanentemente removidos. Esta ação é irreversível.
              </p>
            </div>
            <Button
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700 shrink-0"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir conta
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Delete account dialog ───────────────────────────────────────────── */}
      <DeleteAccountDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      />

    </div>
  )
}
